import { type PermissionCode } from '@/lib/permissions';
import { REPORT_VIEW_PERMISSIONS } from '@/lib/report-permissions';

export type CustomReportSourceKey =
  | 'contributions'
  | 'members'
  | 'expenses'
  | 'pledges'
  | 'welfare_requests'
  | 'projects'
  | 'groups'
  | 'check_ins'
  | 'attendance_sessions'
  | 'pastoral_visits'
  | 'pastoral_appointments'
  | 'prayer_requests'
  | 'campaigns'
  | 'assets'
  | 'asset_rentals';

export type CustomReportFieldType = 'text' | 'number' | 'date' | 'boolean';

export type CustomReportFieldValueSet =
  | 'contribution_type'
  | 'contribution_method'
  | 'member_type'
  | 'member_status'
  | 'gender'
  | 'expense_status'
  | 'expense_method'
  | 'expense_payee_kind'
  | 'pledge_status'
  | 'pledge_frequency'
  | 'project_status'
  | 'welfare_status'
  | 'group_type'
  | 'group_category'
  | 'check_in_method'
  | 'attendance_session_method'
  | 'attendance_session_status'
  | 'pastoral_visit_kind'
  | 'pastoral_appointment_kind'
  | 'pastoral_appointment_status'
  | 'prayer_category'
  | 'prayer_status'
  | 'campaign_status'
  | 'message_audience_type'
  | 'asset_kind'
  | 'asset_condition'
  | 'asset_status'
  | 'asset_rental_status';

export type CustomReportFilterOptionSource =
  | 'fund'
  | 'member'
  | 'department'
  | 'unit'
  | 'committee'
  | 'expense_category'
  | 'project';

/** Legacy saved reports may still reference ID columns — map them to display columns. */
export const CUSTOM_REPORT_COLUMN_ALIASES: Record<string, string> = {
  departmentId: 'departmentName',
  fundId: 'fundName',
  memberId: 'memberName',
  categoryId: 'categoryName',
  projectId: 'projectName',
  visitedByMemberId: 'visitedByName',
  withMemberId: 'withMemberName',
  assignedToMemberId: 'assignedToName',
  custodianMemberId: 'custodianName',
  borrowerMemberId: 'borrowerMemberName',
  assetId: 'assetName',
};

export interface CustomReportFieldDefinition {
  key: string;
  type: CustomReportFieldType;
  valueSet?: CustomReportFieldValueSet;
  filterOptionSource?: CustomReportFilterOptionSource;
  filterOnly?: boolean;
}

export interface CustomReportSourceDefinition {
  key: CustomReportSourceKey;
  module: string;
  permission: PermissionCode;
  fields: CustomReportFieldDefinition[];
}

/** Field keys per source — labels come from locale `custom.fields.*`. */
const SOURCE_FIELDS: Record<CustomReportSourceKey, CustomReportFieldDefinition[]> = {
  contributions: [
    { key: 'contributionDate', type: 'date' },
    { key: 'amountMinor', type: 'number' },
    { key: 'currency', type: 'text' },
    { key: 'contributionType', type: 'text', valueSet: 'contribution_type' },
    { key: 'method', type: 'text', valueSet: 'contribution_method' },
    { key: 'fundId', type: 'text', filterOptionSource: 'fund', filterOnly: true },
    { key: 'fundName', type: 'text' },
    { key: 'giverName', type: 'text' },
    { key: 'memberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'reference', type: 'text' },
    { key: 'note', type: 'text' },
    { key: 'receiptNumber', type: 'number' },
    { key: 'isOnline', type: 'boolean' },
    { key: 'isVoided', type: 'boolean' },
    { key: 'createdAt', type: 'date' },
  ],
  members: [
    { key: 'firstName', type: 'text' },
    { key: 'lastName', type: 'text' },
    { key: 'membershipNumber', type: 'text' },
    { key: 'memberType', type: 'text', valueSet: 'member_type' },
    { key: 'status', type: 'text', valueSet: 'member_status' },
    { key: 'gender', type: 'text', valueSet: 'gender' },
    { key: 'joinedAt', type: 'date' },
    { key: 'email', type: 'text' },
    { key: 'phone', type: 'text' },
    { key: 'departmentId', type: 'text', filterOptionSource: 'department', filterOnly: true },
    { key: 'departmentName', type: 'text' },
    { key: 'unitId', type: 'text', filterOptionSource: 'unit', filterOnly: true },
    { key: 'groupId', type: 'text', filterOptionSource: 'committee', filterOnly: true },
    { key: 'city', type: 'text' },
    { key: 'region', type: 'text' },
    { key: 'country', type: 'text' },
    { key: 'createdAt', type: 'date' },
  ],
  expenses: [
    { key: 'expenseDate', type: 'date' },
    { key: 'amountMinor', type: 'number' },
    { key: 'currency', type: 'text' },
    { key: 'payee', type: 'text' },
    { key: 'payeeKind', type: 'text', valueSet: 'expense_payee_kind' },
    { key: 'description', type: 'text' },
    { key: 'status', type: 'text', valueSet: 'expense_status' },
    { key: 'method', type: 'text', valueSet: 'expense_method' },
    { key: 'categoryId', type: 'text', filterOptionSource: 'expense_category', filterOnly: true },
    { key: 'categoryName', type: 'text' },
    { key: 'fundId', type: 'text', filterOptionSource: 'fund', filterOnly: true },
    { key: 'fundName', type: 'text' },
    { key: 'accountName', type: 'text' },
    { key: 'memberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'memberName', type: 'text' },
    { key: 'reference', type: 'text' },
    { key: 'createdAt', type: 'date' },
  ],
  pledges: [
    { key: 'memberName', type: 'text' },
    { key: 'memberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'fundId', type: 'text', filterOptionSource: 'fund', filterOnly: true },
    { key: 'fundName', type: 'text' },
    { key: 'projectId', type: 'text', filterOptionSource: 'project', filterOnly: true },
    { key: 'projectName', type: 'text' },
    { key: 'totalAmountMinor', type: 'number' },
    { key: 'currency', type: 'text' },
    { key: 'frequency', type: 'text', valueSet: 'pledge_frequency' },
    { key: 'instalmentAmountMinor', type: 'number' },
    { key: 'startDate', type: 'date' },
    { key: 'endDate', type: 'date' },
    { key: 'status', type: 'text', valueSet: 'pledge_status' },
    { key: 'fulfilledAmountMinor', type: 'number' },
    { key: 'remainingAmountMinor', type: 'number' },
    { key: 'overFulfilled', type: 'boolean' },
    { key: 'notes', type: 'text' },
    { key: 'createdAt', type: 'date' },
  ],
  welfare_requests: [
    { key: 'memberName', type: 'text' },
    { key: 'memberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'requestType', type: 'text' },
    { key: 'description', type: 'text' },
    { key: 'requestedAmountMinor', type: 'number' },
    { key: 'approvedAmountMinor', type: 'number' },
    { key: 'currency', type: 'text' },
    { key: 'status', type: 'text', valueSet: 'welfare_status' },
    { key: 'disbursedAt', type: 'date' },
    { key: 'createdAt', type: 'date' },
  ],
  projects: [
    { key: 'name', type: 'text' },
    { key: 'slug', type: 'text' },
    { key: 'targetAmountMinor', type: 'number' },
    { key: 'raisedAmountMinor', type: 'number' },
    { key: 'currency', type: 'text' },
    { key: 'progressPercent', type: 'number' },
    { key: 'startDate', type: 'date' },
    { key: 'deadline', type: 'date' },
    { key: 'status', type: 'text', valueSet: 'project_status' },
    { key: 'isPubliclyVisible', type: 'boolean' },
    { key: 'fundId', type: 'text', filterOptionSource: 'fund', filterOnly: true },
    { key: 'fundName', type: 'text' },
    { key: 'createdAt', type: 'date' },
  ],
  groups: [
    { key: 'name', type: 'text' },
    { key: 'description', type: 'text' },
    { key: 'type', type: 'text', valueSet: 'group_type' },
    { key: 'category', type: 'text', valueSet: 'group_category' },
    { key: 'isOpen', type: 'boolean' },
    { key: 'meetingSchedule', type: 'text' },
    { key: 'meetingVenueName', type: 'text' },
    { key: 'gender', type: 'text', valueSet: 'gender' },
    { key: 'minAge', type: 'number' },
    { key: 'maxAge', type: 'number' },
    { key: 'isActive', type: 'boolean' },
    { key: 'memberCount', type: 'number' },
    { key: 'createdAt', type: 'date' },
  ],
  check_ins: [
    { key: 'checkedInAt', type: 'date' },
    { key: 'firstName', type: 'text' },
    { key: 'lastName', type: 'text' },
    { key: 'memberName', type: 'text' },
    { key: 'memberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'programName', type: 'text' },
    { key: 'method', type: 'text', valueSet: 'check_in_method' },
  ],
  attendance_sessions: [
    { key: 'programName', type: 'text' },
    { key: 'occurrenceDate', type: 'date' },
    { key: 'programKind', type: 'text' },
    { key: 'venueName', type: 'text' },
    { key: 'method', type: 'text', valueSet: 'attendance_session_method' },
    { key: 'status', type: 'text', valueSet: 'attendance_session_status' },
    { key: 'opensAt', type: 'date' },
    { key: 'closesAt', type: 'date' },
    { key: 'headcount', type: 'number' },
    { key: 'kioskCount', type: 'number' },
    { key: 'createdAt', type: 'date' },
  ],
  pastoral_visits: [
    { key: 'memberName', type: 'text' },
    { key: 'memberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'visitedByName', type: 'text' },
    { key: 'visitedByMemberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'visitDate', type: 'date' },
    { key: 'kind', type: 'text', valueSet: 'pastoral_visit_kind' },
    { key: 'summary', type: 'text' },
    { key: 'followUpNeeded', type: 'boolean' },
    { key: 'createdAt', type: 'date' },
  ],
  pastoral_appointments: [
    { key: 'memberName', type: 'text' },
    { key: 'memberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'withMemberName', type: 'text' },
    { key: 'withMemberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'kind', type: 'text', valueSet: 'pastoral_appointment_kind' },
    { key: 'startsAt', type: 'date' },
    { key: 'endsAt', type: 'date' },
    { key: 'location', type: 'text' },
    { key: 'purpose', type: 'text' },
    { key: 'status', type: 'text', valueSet: 'pastoral_appointment_status' },
    { key: 'createdAt', type: 'date' },
  ],
  prayer_requests: [
    { key: 'category', type: 'text', valueSet: 'prayer_category' },
    { key: 'status', type: 'text', valueSet: 'prayer_status' },
    { key: 'isAnonymous', type: 'boolean' },
    { key: 'assignedToName', type: 'text' },
    { key: 'assignedToMemberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'respondedAt', type: 'date' },
    { key: 'createdAt', type: 'date' },
  ],
  campaigns: [
    { key: 'name', type: 'text' },
    { key: 'status', type: 'text', valueSet: 'campaign_status' },
    { key: 'audienceType', type: 'text', valueSet: 'message_audience_type' },
    { key: 'scheduledAt', type: 'date' },
    { key: 'sentAt', type: 'date' },
    { key: 'resolvedCount', type: 'number' },
    { key: 'deliveredCount', type: 'number' },
    { key: 'failedCount', type: 'number' },
    { key: 'estimatedRecipients', type: 'number' },
    { key: 'estimatedCostMinor', type: 'number' },
    { key: 'actualCostMinor', type: 'number' },
    { key: 'currency', type: 'text' },
    { key: 'isEssential', type: 'boolean' },
    { key: 'createdAt', type: 'date' },
  ],
  assets: [
    { key: 'name', type: 'text' },
    { key: 'kind', type: 'text', valueSet: 'asset_kind' },
    { key: 'category', type: 'text' },
    { key: 'assetTag', type: 'text' },
    { key: 'serialNumber', type: 'text' },
    { key: 'manufacturer', type: 'text' },
    { key: 'model', type: 'text' },
    { key: 'acquiredOn', type: 'date' },
    { key: 'acquisitionCostMinor', type: 'number' },
    { key: 'acquisitionCurrency', type: 'text' },
    { key: 'condition', type: 'text', valueSet: 'asset_condition' },
    { key: 'status', type: 'text', valueSet: 'asset_status' },
    { key: 'locationNote', type: 'text' },
    { key: 'custodianName', type: 'text' },
    { key: 'custodianMemberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'createdAt', type: 'date' },
  ],
  asset_rentals: [
    { key: 'assetName', type: 'text' },
    { key: 'assetId', type: 'text', filterOnly: true },
    { key: 'borrowerName', type: 'text' },
    { key: 'borrowerMemberName', type: 'text' },
    { key: 'borrowerMemberId', type: 'text', filterOptionSource: 'member', filterOnly: true },
    { key: 'borrowerPhone', type: 'text' },
    { key: 'purpose', type: 'text' },
    { key: 'startsOn', type: 'date' },
    { key: 'dueOn', type: 'date' },
    { key: 'returnedOn', type: 'date' },
    { key: 'chargeMinor', type: 'number' },
    { key: 'chargeCurrency', type: 'text' },
    { key: 'status', type: 'text', valueSet: 'asset_rental_status' },
    { key: 'createdAt', type: 'date' },
  ],
};

export const CUSTOM_REPORT_SOURCES: Record<CustomReportSourceKey, CustomReportSourceDefinition> = {
  contributions: {
    key: 'contributions',
    module: 'giving',
    permission: REPORT_VIEW_PERMISSIONS.GIVING,
    fields: SOURCE_FIELDS.contributions,
  },
  members: {
    key: 'members',
    module: 'people',
    permission: REPORT_VIEW_PERMISSIONS.PEOPLE,
    fields: SOURCE_FIELDS.members,
  },
  expenses: {
    key: 'expenses',
    module: 'spending',
    permission: REPORT_VIEW_PERMISSIONS.SPENDING,
    fields: SOURCE_FIELDS.expenses,
  },
  pledges: {
    key: 'pledges',
    module: 'giving',
    permission: REPORT_VIEW_PERMISSIONS.GIVING,
    fields: SOURCE_FIELDS.pledges,
  },
  welfare_requests: {
    key: 'welfare_requests',
    module: 'welfare',
    permission: REPORT_VIEW_PERMISSIONS.WELFARE,
    fields: SOURCE_FIELDS.welfare_requests,
  },
  projects: {
    key: 'projects',
    module: 'projects',
    permission: REPORT_VIEW_PERMISSIONS.PROJECTS,
    fields: SOURCE_FIELDS.projects,
  },
  groups: {
    key: 'groups',
    module: 'groups',
    permission: REPORT_VIEW_PERMISSIONS.GROUPS,
    fields: SOURCE_FIELDS.groups,
  },
  check_ins: {
    key: 'check_ins',
    module: 'attendance',
    permission: REPORT_VIEW_PERMISSIONS.ATTENDANCE,
    fields: SOURCE_FIELDS.check_ins,
  },
  attendance_sessions: {
    key: 'attendance_sessions',
    module: 'attendance',
    permission: REPORT_VIEW_PERMISSIONS.ATTENDANCE,
    fields: SOURCE_FIELDS.attendance_sessions,
  },
  pastoral_visits: {
    key: 'pastoral_visits',
    module: 'pastoral',
    permission: REPORT_VIEW_PERMISSIONS.PASTORAL,
    fields: SOURCE_FIELDS.pastoral_visits,
  },
  pastoral_appointments: {
    key: 'pastoral_appointments',
    module: 'pastoral',
    permission: REPORT_VIEW_PERMISSIONS.PASTORAL,
    fields: SOURCE_FIELDS.pastoral_appointments,
  },
  prayer_requests: {
    key: 'prayer_requests',
    module: 'prayer',
    permission: REPORT_VIEW_PERMISSIONS.PRAYER,
    fields: SOURCE_FIELDS.prayer_requests,
  },
  campaigns: {
    key: 'campaigns',
    module: 'messaging',
    permission: REPORT_VIEW_PERMISSIONS.MESSAGING,
    fields: SOURCE_FIELDS.campaigns,
  },
  assets: {
    key: 'assets',
    module: 'assets',
    permission: REPORT_VIEW_PERMISSIONS.ASSETS,
    fields: SOURCE_FIELDS.assets,
  },
  asset_rentals: {
    key: 'asset_rentals',
    module: 'assets',
    permission: REPORT_VIEW_PERMISSIONS.ASSETS,
    fields: SOURCE_FIELDS.asset_rentals,
  },
};

export const CUSTOM_REPORT_SOURCE_KEYS = Object.keys(
  CUSTOM_REPORT_SOURCES,
) as CustomReportSourceKey[];

export const getCustomReportSource = (key: string): CustomReportSourceDefinition | undefined =>
  CUSTOM_REPORT_SOURCES[key as CustomReportSourceKey];

export const operatorsForField = (
  field: CustomReportFieldDefinition,
): readonly string[] => {
  if (field.type === 'boolean') return ['eq'];
  if (field.type === 'number') return ['eq', 'neq', 'gt', 'lt', 'empty', 'not_empty'];
  if (field.type === 'date') return ['eq', 'gte', 'lte', 'empty', 'not_empty'];
  if (field.valueSet || field.filterOptionSource) return ['eq', 'neq', 'empty', 'not_empty'];
  return ['eq', 'neq', 'contains', 'empty', 'not_empty'];
};
